// SPDX-License-Identifier: GPL-3.0-or-later
//
// Cheap, deterministic heuristic for catching CV claims the AI might have
// invented. We extract "fact-shaped tokens" from each bullet — capitalised
// proper nouns, numbers, percentages, and a few unit patterns — and check
// whether they appear (case-insensitively) anywhere in the source profile
// text. Anything missing gets flagged.
//
// False positives are expected (generic capitalised words, abstract numbers
// the user genuinely wrote). The UI presents this as a "review" panel, not
// a hard block.

import type { CvDoc } from './schemas';

// Sentence-start capitalised words shouldn't auto-flag — verbs and common
// English get capitalised after every period. We rely on this list plus a
// "first word of sentence" rule.
const COMMON_CAPITALISED = new Set([
  'A', 'An', 'And', 'As', 'At', 'But', 'By', 'For', 'From', 'I', 'In', 'It',
  'Of', 'On', 'Or', 'So', 'The', 'This', 'That', 'To', 'Up', 'We', 'With',
  'Built', 'Created', 'Designed', 'Developed', 'Drove', 'Established',
  'Founded', 'Grew', 'Implemented', 'Improved', 'Increased', 'Launched',
  'Led', 'Managed', 'Owned', 'Reduced', 'Rebuilt', 'Refactored', 'Scaled',
  'Shipped', 'Streamlined', 'Wrote', 'Delivered', 'Architected', 'Coached',
  'Mentored', 'Drove', 'Drove', 'Saved', 'Spent',
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
  'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December',
]);

export interface FlaggedToken {
  token: string;
  kind: 'proper-noun' | 'number';
}

export interface FlaggedBullet {
  company: string;
  role: string;
  bullet: string;
  tokens: FlaggedToken[];
}

export interface HallucinationReport {
  flagged: FlaggedBullet[];
  // Total bullets scanned — useful for showing "0 of 14 bullets flagged".
  totalBullets: number;
}

function normalize(s: string): string {
  return s.toLowerCase().normalize('NFKD').replace(/\p{Diacritic}/gu, '');
}

function profileSet(profile: string): { text: string } {
  return { text: normalize(profile) };
}

function profileContains(haystack: { text: string }, needle: string): boolean {
  const n = normalize(needle);
  if (n.length < 2) return true; // too short to be useful evidence
  return haystack.text.includes(n);
}

function isFirstWordOfSentence(bullet: string, position: number): boolean {
  // Match: start of string, or after . ! ? : ; followed by whitespace.
  const before = bullet.slice(0, position).trimEnd();
  if (before.length === 0) return true;
  const last = before.slice(-1);
  return /[.!?:;]/.test(last);
}

function extractProperNouns(bullet: string): { token: string; position: number }[] {
  const out: { token: string; position: number }[] = [];
  // Sequences of CapitalisedWords (incl. acronyms, hyphenated, with dots)
  const re = /\b([A-Z][\w.+#\-]{1,}(?:\s+[A-Z][\w.+#\-]{1,}){0,2})\b/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(bullet))) {
    out.push({ token: m[1]!, position: m.index });
  }
  return out;
}

function extractNumbers(bullet: string): { token: string; position: number }[] {
  const out: { token: string; position: number }[] = [];
  // Numbers with optional commas, decimals, percent, currency, K/M/B suffix.
  // Skips single digits unless they're percentages — too noisy.
  const re = /\b(\$?\d{1,3}(?:[,\d]{2,})?(?:\.\d+)?[%KMBkmb]?|\d+(?:\.\d+)?%)\b/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(bullet))) {
    const tok = m[1]!;
    // Skip standalone 1-digit numbers without %.
    if (/^\d$/.test(tok)) continue;
    out.push({ token: tok, position: m.index });
  }
  return out;
}

export function checkCvHallucinations(doc: CvDoc, profileText: string): HallucinationReport {
  const haystack = profileSet(profileText);
  const flagged: FlaggedBullet[] = [];
  let total = 0;

  for (const exp of doc.experience) {
    // Company + role names themselves: trust them if present in profile, else
    // flag the very first bullet as a hint about the role itself.
    const expSupported =
      profileContains(haystack, exp.company) &&
      (exp.role.length < 3 || profileContains(haystack, exp.role));

    for (const bullet of exp.bullets) {
      total++;
      const tokens: FlaggedToken[] = [];

      // Proper-noun candidates
      for (const { token, position } of extractProperNouns(bullet)) {
        if (isFirstWordOfSentence(bullet, position)) continue;
        if (COMMON_CAPITALISED.has(token)) continue;
        // If exp.company is present and the token matches the company, treat
        // as supported regardless of profile (it's contextually clear).
        if (profileContains(haystack, token)) continue;
        if (normalize(token) === normalize(exp.company)) continue;
        tokens.push({ token, kind: 'proper-noun' });
      }

      // Numbers
      for (const { token } of extractNumbers(bullet)) {
        const stripped = token.replace(/[$%KMBkmb,]/g, '');
        if (profileContains(haystack, stripped)) continue;
        tokens.push({ token, kind: 'number' });
      }

      // If the company itself wasn't even in the profile, flag the first
      // bullet's whole company line so the user sees the issue.
      if (!expSupported && tokens.length === 0) {
        tokens.push({ token: exp.company, kind: 'proper-noun' });
      }

      if (tokens.length > 0) {
        flagged.push({
          company: exp.company,
          role: exp.role,
          bullet,
          tokens,
        });
      }
    }
  }

  return { flagged, totalBullets: total };
}
